import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconX, IconDeviceFloppy, IconUserSearch,
  IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm,
} from '@tabler/icons-react';
import api from '../api/api';

const WEATHER_OPTIONS = [
  { code: 'sunny',  label: '맑음',   Icon: IconSun,        color: '#f59e0b' },
  { code: 'cloudy', label: '흐림',   Icon: IconCloud,      color: '#6b7280' },
  { code: 'rain',   label: '비',     Icon: IconCloudRain,  color: '#3b82f6' },
  { code: 'snow',   label: '눈',     Icon: IconSnowflake,  color: '#60a5fa' },
  { code: 'wind',   label: '바람',   Icon: IconWind,       color: '#8b5cf6' },
  { code: 'storm',  label: '폭풍',   Icon: IconCloudStorm, color: '#ef4444' },
];

const STATUS_MAP = {
  PENDING:   { label: '대기' },
  CANCELLED: { label: '취소' },
  COMPLETED: { label: '완료' },
};

const EMPTY_FORM = {
  golfCourseId: '', reservedUserId: '', reserverName: '', reserverTel: '',
  reservedDate: '', reservedTime: '', playerCount: 4, status: 'PENDING',
  memo: '', adminMemo: '', greenFee: '', weather: '', temperature: '',
  gameType: '일반', useYn: 'Y', sections: [],
};

export default function BookingFormModal({ onClose, onSaved, showToast, apiPrefix = '/golf', editData = null }) {
  const isEdit = !!editData;

  const [form, setForm] = useState(() => {
    if (editData) {
      return {
        golfCourseId: editData.golfCourseId ? String(editData.golfCourseId) : '',
        reservedUserId: editData.reservedUserId || '',
        reserverName: editData.reserverName || '',
        reserverTel: editData.reserverTel || '',
        reservedDate: editData.reservedDate || '',
        reservedTime: editData.reservedTime ? editData.reservedTime.slice(0, 5) : '',
        playerCount: editData.playerCount || 4,
        status: editData.status || 'PENDING',
        memo: editData.memo || '', adminMemo: editData.adminMemo || '',
        greenFee: editData.greenFee != null ? String(editData.greenFee) : '',
        weather: editData.weather || '', temperature: editData.temperature != null ? String(editData.temperature) : '',
        gameType: editData.gameType || '일반', useYn: editData.useYn || 'Y',
        sections: editData.sections ? editData.sections.map(s => ({ sectionId: s.sectionId, playOrder: s.playOrder })) : [],
      };
    }
    return { ...EMPTY_FORM, reservedDate: new Date().toLocaleDateString('sv') };
  });
  const [courses, setCourses] = useState([]);
  const [courseSections, setCourseSections] = useState([]);
  const [saving, setSaving] = useState(false);

  // 골프장 자동완성
  const [courseInput, setCourseInput] = useState('');
  const [showCourseList, setShowCourseList] = useState(false);
  const courseRef = useRef(null);

  // 회원 검색
  const [memberInput, setMemberInput] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const memberRef = useRef(null);
  const memberTimer = useRef(null);

  useEffect(() => {
    api.get(`${apiPrefix}/courses`).then(res => {
      const list = res.data?.content || res.data || [];
      setCourses(list);
      // 수정 모드: 골프장명 표시 + 섹션 로드
      if (editData?.golfCourseId) {
        const c = list.find(c => String(c.id) === String(editData.golfCourseId));
        if (c) setCourseInput(c.name);
        api.get(`${apiPrefix}/courses/${editData.golfCourseId}/sections`)
          .then(r => setCourseSections(r.data || [])).catch(() => {});
      }
    }).catch(() => {});
  }, [apiPrefix]);

  // 바깥 클릭
  useEffect(() => {
    const handler = (e) => {
      if (courseRef.current && !courseRef.current.contains(e.target)) setShowCourseList(false);
      if (memberRef.current && !memberRef.current.contains(e.target)) setShowMemberList(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredCourses = courseInput
    ? courses.filter(c => c.name.toLowerCase().includes(courseInput.toLowerCase())).slice(0, 30)
    : courses.slice(0, 30);

  const selectCourse = async (c) => {
    setCourseInput(c.name);
    setShowCourseList(false);
    setForm(f => ({ ...f, golfCourseId: String(c.id), sections: [] }));
    try {
      const res = await api.get(`${apiPrefix}/courses/${c.id}/sections`);
      setCourseSections(res.data || []);
    } catch { setCourseSections([]); }
  };

  const handleMemberInput = (val) => {
    setMemberInput(val);
    setShowMemberList(true);
    if (!val) setForm(f => ({ ...f, reservedUserId: '' }));
    clearTimeout(memberTimer.current);
    if (val.trim().length < 1) { setMemberResults([]); return; }
    memberTimer.current = setTimeout(async () => {
      setMemberSearching(true);
      try {
        const res = await api.get('/messages/users/search', { params: { keyword: val } });
        setMemberResults(res.data || []);
      } catch { setMemberResults([]); }
      finally { setMemberSearching(false); }
    }, 300);
  };

  const selectMember = (u) => {
    setMemberInput(`#${u.id}  ${u.name}${u.nickname ? '  (' + u.nickname + ')' : ''}`);
    setShowMemberList(false);
    setMemberResults([]);
    setForm(f => ({ ...f, reservedUserId: u.id, reserverName: f.reserverName || u.name || '' }));
  };

  const addSection = (sectionId) => {
    setForm(f => ({ ...f, sections: [...f.sections, { sectionId, playOrder: f.sections.length + 1 }] }));
  };
  const removeOneSection = (sectionId) => {
    setForm(f => {
      const idx = f.sections.findLastIndex(s => s.sectionId === sectionId);
      if (idx < 0) return f;
      const next = f.sections.filter((_, i) => i !== idx);
      return { ...f, sections: next.map((s, i) => ({ ...s, playOrder: i + 1 })) };
    });
  };

  const handleSave = async () => {
    if (!form.golfCourseId) { showToast('골프장을 선택하세요.', 'danger'); return; }
    if (!form.reservedDate || !form.reservedTime) { showToast('예약 날짜/시간을 입력하세요.', 'danger'); return; }
    setSaving(true);
    try {
      const payload = {
        golfCourseId: Number(form.golfCourseId),
        reservedUserId: form.reservedUserId ? Number(form.reservedUserId) : null,
        reserverName: form.reserverName || null,
        reserverTel: form.reserverTel || null,
        reservedDate: form.reservedDate,
        reservedTime: form.reservedTime.length === 5 ? form.reservedTime + ':00' : form.reservedTime,
        playerCount: Number(form.playerCount) || 4,
        status: form.status || 'PENDING',
        memo: form.memo || null, adminMemo: form.adminMemo || null,
        greenFee: form.greenFee !== '' ? form.greenFee : null,
        weather: form.weather || null, temperature: form.temperature || null,
        gameType: form.gameType || null, useYn: form.useYn || 'Y',
        sections: form.sections,
      };
      if (isEdit) {
        await api.put(`${apiPrefix}/reservations/${editData.id}`, payload);
        showToast('부킹이 수정되었습니다.');
      } else {
        await api.post(`${apiPrefix}/reservations`, payload);
        showToast('부킹이 등록되었습니다.');
      }
      onSaved?.();
      onClose();
    } catch {
      showToast(isEdit ? '수정에 실패했습니다.' : '등록에 실패했습니다.', 'danger');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 540 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isEdit ? '부킹 수정' : '부킹 추가'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="row g-2">

              {/* 날짜 | 티타임 */}
              <div className="col-6">
                <label className="form-label mb-1">예약 날짜 <span className="text-danger">*</span></label>
                <input type="date" className="form-control form-control-sm" value={form.reservedDate}
                  onChange={e => setForm(f => ({ ...f, reservedDate: e.target.value }))} />
              </div>
              <div className="col-6">
                <label className="form-label mb-1">티타임 <span className="text-danger">*</span></label>
                <input type="text" className="form-control form-control-sm" placeholder="0930 또는 09:30" maxLength={5}
                  value={form.reservedTime}
                  onChange={e => setForm(f => ({ ...f, reservedTime: e.target.value.replace(/[^0-9:]/g, '') }))}
                  onBlur={e => {
                    const d = e.target.value.replace(/\D/g, '');
                    if (d.length === 3) setForm(f => ({ ...f, reservedTime: `0${d[0]}:${d.slice(1)}` }));
                    else if (d.length === 4) setForm(f => ({ ...f, reservedTime: `${d.slice(0,2)}:${d.slice(2)}` }));
                  }}
                />
              </div>

              {/* 골프장 + 코스 (한 줄: col-6 | col-3 | col-3) */}
              <div className="col-6">
                    <label className="form-label mb-1">골프장 <span className="text-danger">*</span></label>
                    <div ref={courseRef} style={{ position: 'relative' }}>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="골프장명 입력 또는 검색" value={courseInput} autoComplete="off"
                        onChange={e => { setCourseInput(e.target.value); setShowCourseList(true); if (!e.target.value) { setForm(f => ({ ...f, golfCourseId: '' })); setCourseSections([]); } }}
                        onFocus={() => setShowCourseList(true)} />
                      {showCourseList && filteredCourses.length > 0 && (
                        <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: 200, overflowY: 'auto', margin: 0, padding: 0, listStyle: 'none', background: 'var(--tblr-card-bg, #fff)', border: '1px solid var(--tblr-border-color)', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                          {filteredCourses.map(c => (
                            <li key={c.id} style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13, background: String(form.golfCourseId) === String(c.id) ? 'var(--tblr-primary-lt)' : undefined }}
                              onMouseDown={() => selectCourse(c)}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--tblr-active-bg, #f1f5f9)'}
                              onMouseLeave={e => e.currentTarget.style.background = String(form.golfCourseId) === String(c.id) ? 'var(--tblr-primary-lt)' : ''}>
                              {c.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
              </div>
              {courseSections.length > 0 && (
                <>
                  <div className="col-3">
                    <label className="form-label mb-1">전반</label>
                    <select className="form-select form-select-sm"
                      value={form.sections[0]?.sectionId || ''}
                      onChange={e => {
                        const secId = e.target.value ? Number(e.target.value) : null;
                        setForm(f => {
                          const second = f.sections[1] || null;
                          if (secId) {
                            const first = { sectionId: secId, playOrder: 1 };
                            return { ...f, sections: second ? [first, second] : [first] };
                          }
                          return { ...f, sections: second ? [{ ...second, playOrder: 1 }] : [] };
                        });
                      }}>
                      <option value="">선택</option>
                      {courseSections.filter(s => s.useYn === 'Y').map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.holesCount || 9}홀)</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-3">
                    <label className="form-label mb-1">후반</label>
                    <select className="form-select form-select-sm"
                      value={form.sections[1]?.sectionId || ''}
                      onChange={e => {
                        const secId = e.target.value ? Number(e.target.value) : null;
                        setForm(f => {
                          const first = f.sections[0] || null;
                          if (secId) {
                            const second = { sectionId: secId, playOrder: 2 };
                            return { ...f, sections: first ? [first, second] : [{ sectionId: secId, playOrder: 1 }] };
                          }
                          return { ...f, sections: first ? [first] : [] };
                        });
                      }}>
                      <option value="">선택</option>
                      {courseSections.filter(s => s.useYn === 'Y').map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.holesCount || 9}홀)</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* 인원 | 상태 | 그린피 */}
              <div className="col-4">
                <label className="form-label mb-1">인원</label>
                <input type="number" className="form-control form-control-sm" min={1} max={20} value={form.playerCount}
                  onChange={e => setForm(f => ({ ...f, playerCount: e.target.value }))} />
              </div>
              <div className="col-4">
                <label className="form-label mb-1">상태</label>
                <select className="form-select form-select-sm" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="col-4">
                <label className="form-label mb-1">그린피</label>
                <input type="text" className="form-control form-control-sm" placeholder="만원 또는 쿠폰 등" value={form.greenFee}
                  onChange={e => setForm(f => ({ ...f, greenFee: e.target.value }))} />
              </div>

              {/* 회원 | 예약자명 | 연락처 */}
              <div className="col-6">
                <label className="form-label mb-1">회원</label>
                <div ref={memberRef} style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 2, display: 'flex', color: '#6c757d' }}>
                    {memberSearching ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <IconUserSearch size={13} />}
                  </span>
                  <input type="text" className="form-control form-control-sm" style={{ paddingLeft: 26, paddingRight: 22 }}
                    placeholder="이름, 닉네임" autoComplete="off" value={memberInput}
                    onChange={e => handleMemberInput(e.target.value)}
                    onFocus={() => memberResults.length > 0 && setShowMemberList(true)} />
                  <button type="button" tabIndex={-1} style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', zIndex: 2, visibility: memberInput ? 'visible' : 'hidden' }}
                    onClick={() => { setMemberInput(''); setMemberResults([]); setShowMemberList(false); setForm(f => ({ ...f, reservedUserId: '' })); }}>
                    <IconX size={12} color="#aaa" />
                  </button>
                  {showMemberList && memberResults.length > 0 && (
                    <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: 180, overflowY: 'auto', margin: 0, padding: 0, listStyle: 'none', background: 'var(--tblr-card-bg, #fff)', border: '1px solid var(--tblr-border-color)', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                      {memberResults.map(u => (
                        <li key={u.id} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                          onMouseDown={() => selectMember(u)}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--tblr-active-bg, #f1f5f9)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <span className="fw-medium">{u.name}</span>
                          {u.nickname && <span className="text-muted ms-1" style={{ fontSize: 11 }}>({u.nickname})</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div className="col-3">
                <label className="form-label mb-1">예약자명</label>
                <input type="text" className="form-control form-control-sm" placeholder="비회원"
                  value={form.reserverName} onChange={e => setForm(f => ({ ...f, reserverName: e.target.value }))} />
              </div>
              <div className="col-3">
                <label className="form-label mb-1">연락처</label>
                <input type="text" className="form-control form-control-sm" placeholder="010-0000-0000"
                  value={form.reserverTel} onChange={e => setForm(f => ({ ...f, reserverTel: e.target.value }))} />
              </div>

              {/* 경기유형 | 유효 | 기온 */}
              <div className="col-4">
                <label className="form-label mb-1">경기유형</label>
                <select className="form-select form-select-sm" value={form.gameType}
                  onChange={e => setForm(f => ({ ...f, gameType: e.target.value }))}>
                  <option value="일반">일반</option><option value="메이저">메이저</option>
                </select>
              </div>
              <div className="col-4">
                <label className="form-label mb-1">유효</label>
                <select className="form-select form-select-sm" value={form.useYn}
                  onChange={e => setForm(f => ({ ...f, useYn: e.target.value }))}>
                  <option value="Y">Y (유효)</option><option value="N">N (무효)</option>
                </select>
              </div>
              <div className="col-4">
                <label className="form-label mb-1">기온</label>
                <input type="text" className="form-control form-control-sm" placeholder="예: 13  또는  4~9"
                  value={form.temperature} onChange={e => setForm(f => ({ ...f, temperature: e.target.value }))} />
              </div>

              {/* 날씨 */}
              <div className="col-12">
                <label className="form-label mb-1">날씨</label>
                <div className="d-flex flex-wrap gap-1">
                  {WEATHER_OPTIONS.map(w => (
                    <button key={w.code} type="button" title={w.label}
                      className={`btn btn-sm ${form.weather === w.code ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ padding: '3px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      onClick={() => setForm(f => ({ ...f, weather: f.weather === w.code ? '' : w.code }))}>
                      <w.Icon size={13} color={form.weather === w.code ? '#fff' : w.color} />
                      <span style={{ fontSize: 12 }}>{w.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 요청사항 */}
              <div className="col-12">
                <label className="form-label mb-1">요청사항</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.memo}
                  onChange={e => setForm(f => ({ ...f, memo: e.target.value }))} />
              </div>

              {/* 관리자 메모 */}
              <div className="col-12">
                <label className="form-label mb-1">관리자 메모</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.adminMemo}
                  onChange={e => setForm(f => ({ ...f, adminMemo: e.target.value }))} />
              </div>

            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-sm" onClick={onClose}><IconX size={14} className="me-1" />취소</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <IconDeviceFloppy size={14} className="me-1" />{saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
